// Header aliasing for resident imports.
//
// The importer only creates residents from columns named exactly "Name" and
// "Email". Real-world HOA spreadsheets use near-miss headers ("Name 1",
// "Owner Name", "E-mail Address", first/last name pairs, ...), which
// previously became custom fields and silently produced a units-only import.
// These helpers detect near-miss headers and rename them so the data maps.

export interface ParsedRow {
  [key: string]: string;
}

export interface HeaderMapping {
  from: string;
  to: 'Name' | 'Email';
}

export interface AliasResult {
  rows: ParsedRow[];
  columns: string[];
  mappings: HeaderMapping[];
}

// Candidate headers, matched case-insensitively after trimming, in priority order.
const NAME_ALIASES = [
  'name 1',
  'full name',
  'owner name',
  'owner 1',
  'homeowner name',
  'homeowner',
  'resident name',
  'resident',
  'owner',
  'primary owner',
  'tenant name',
  'tenant',
];

const EMAIL_ALIASES = [
  'email 1',
  'e-mail',
  'email address',
  'e-mail address',
  'owner email',
  'primary email',
  'contact email',
];

// First/last pairs that combine into a single "Name" value.
const FIRST_LAST_PAIRS: [string, string][] = [
  ['first name', 'last name'],
  ['first', 'last'],
  ['fname', 'lname'],
];

function normalize(header: string): string {
  return header.trim().toLowerCase();
}

function columnHasValues(rows: ParsedRow[], column: string): boolean {
  return rows.some((row) => (row[column] ?? '').trim() !== '');
}

function findColumn(columns: string[], target: string): string | undefined {
  return columns.find((c) => normalize(c) === target);
}

/**
 * Rename near-miss "Name"/"Email" headers so the importer recognizes them.
 * A rename only happens when the canonical column is absent or entirely empty,
 * so real data is never clobbered. Returns the rewritten rows/columns plus the
 * applied mappings for display.
 */
export function applyHeaderAliases(rows: ParsedRow[], columns: string[]): AliasResult {
  const mappings: HeaderMapping[] = [];
  let nextRows = rows;
  let nextColumns = [...columns];

  const renameColumn = (from: string, to: 'Name' | 'Email'): void => {
    nextRows = nextRows.map((row) => {
      const { [from]: value, ...rest } = row;
      return { ...rest, [to]: (value ?? '').trim() || (row[to] ?? '') };
    });
    nextColumns = nextColumns.filter((c) => c !== from && c !== to);
    nextColumns.push(to);
    mappings.push({ from, to });
  };

  // Case-normalize canonical headers first: a populated "name"/"EMAIL" column
  // would otherwise satisfy the usability check while every downstream
  // consumer (row filter, import gate, backend `$row['Name']`) reads the
  // exact-case key — reproducing the silent units-only import.
  (['Name', 'Email'] as const).forEach((canonical) => {
    const existing = findColumn(nextColumns, normalize(canonical));
    if (existing && existing !== canonical) {
      renameColumn(existing, canonical);
    }
  });

  const canonicalIsUsable = (canonical: 'Name' | 'Email'): boolean => {
    const existing = findColumn(nextColumns, normalize(canonical));
    return Boolean(existing && columnHasValues(nextRows, existing));
  };

  if (!canonicalIsUsable('Name')) {
    // Prefer combining a first/last pair when both columns exist with values.
    const pair = FIRST_LAST_PAIRS.map(
      ([first, last]) => [findColumn(nextColumns, first), findColumn(nextColumns, last)] as const
    ).find(([first, last]) => first && last && columnHasValues(nextRows, first));

    if (pair) {
      const [firstCol, lastCol] = pair as [string, string];
      nextRows = nextRows.map((row) => {
        const { [firstCol]: first, [lastCol]: last, ...rest } = row;
        return { ...rest, Name: [first, last].map((v) => (v ?? '').trim()).filter(Boolean).join(' ') };
      });
      nextColumns = nextColumns.filter((c) => c !== firstCol && c !== lastCol && c !== 'Name');
      nextColumns.push('Name');
      mappings.push({ from: `${firstCol} + ${lastCol}`, to: 'Name' });
    } else {
      const alias = NAME_ALIASES.map((a) => findColumn(nextColumns, a)).find(
        (c): c is string => Boolean(c && columnHasValues(nextRows, c))
      );
      if (alias) renameColumn(alias, 'Name');
    }
  }

  if (!canonicalIsUsable('Email')) {
    const alias = EMAIL_ALIASES.map((a) => findColumn(nextColumns, a)).find(
      (c): c is string => Boolean(c && columnHasValues(nextRows, c))
    );
    if (alias) renameColumn(alias, 'Email');
  }

  return { rows: nextRows, columns: nextColumns, mappings };
}

export type ImportGate =
  | { level: 'ok' }
  | { level: 'warning'; message: string }
  | { level: 'blocked'; message: string };

/**
 * Decide whether the import should proceed. Blocks when the file clearly
 * contains people data (emails or names) but zero residents would be created —
 * the exact silent failure that produces a units-only import. A file with no
 * people data at all is a legitimate units-only import and passes with a note.
 */
export function evaluateImportGate(rows: ParsedRow[]): ImportGate {
  if (rows.length === 0) return { level: 'ok' };

  const residentCount = rows.filter((r) => r['Name']?.trim() && r['Email']?.trim()).length;
  const partialCount = rows.filter(
    (r) => Boolean(r['Name']?.trim()) !== Boolean(r['Email']?.trim())
  ).length;
  const emailCount = rows.filter((r) => r['Email']?.trim()).length;

  // A file with no email data at all is a legitimate units-plus-names roster —
  // warn (no residents will be created) but let it through. Only hard-block
  // when emails ARE present and still no resident would be created: that's a
  // column-mismatch failure, not intent.
  if (residentCount === 0 && partialCount > 0 && emailCount === 0) {
    return {
      level: 'warning',
      message:
        `This file has no email addresses, so no residents will be created — ` +
        `${partialCount} row${partialCount > 1 ? 's' : ''} will import as units only. ` +
        'Add an "Email" column to import people.',
    };
  }

  if (residentCount === 0 && partialCount > 0) {
    return {
      level: 'blocked',
      message:
        `${partialCount} row${partialCount > 1 ? 's have' : ' has'} a name or email but not both, ` +
        'so this import would create 0 residents. Rename your columns to "Name" and "Email" ' +
        '(or use the template) so people are imported.',
    };
  }

  if (partialCount > 0) {
    return {
      level: 'warning',
      message:
        `${partialCount} row${partialCount > 1 ? 's' : ''} with a name or email but not both ` +
        `will be imported as units only — no resident will be created for ${partialCount > 1 ? 'them' : 'it'}.`,
    };
  }

  return { level: 'ok' };
}
